import React, { useState, useEffect } from 'react';
import './Client.css';
import { API_URL } from './API';

const Client = ({ onBack, onClientAdded, editClient = null, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    gst_no: '',
    name: '',
    contact_number: '',
    email: '',
    pan_no: '',
    state: '',
    address: '',
    bank_name: '',
    bank_account_number: '',
    ifsc_code: '',
    client_logo: null
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEditMode && editClient) {
      setFormData({
        client_name: editClient.client_name || '',
        gst_no: editClient.gst_no || '',
        name: editClient.name || '',
        contact_number: editClient.contact_number || '',
        email: editClient.email || '',
        pan_no: editClient.pan_no || '',
        state: editClient.state || '',
        address: editClient.address || '',
        bank_name: editClient.bank_name || '',
        bank_account_number: editClient.bank_account_number || '',
        ifsc_code: editClient.ifsc_code || '',
        client_logo: editClient.client_logo || null
      });
      
      if (editClient.client_logo) {
        if (editClient.client_logo.startsWith('data:image/')) {
          setLogoPreview(editClient.client_logo);
        } else {
          const logoUrl = `${API_URL}/${editClient.client_logo}`;
          setLogoPreview(logoUrl);
        }
      }
    }
  }, [isEditMode, editClient]);

  

const handleInputChange = (e) => {
  const { name, value } = e.target;
  
  if (name === 'contact_number') {
    
    const digitsOnly = value.replace(/\D/g, '');
    setFormData(prev => ({
      ...prev,
      [name]: digitsOnly
    }));
  } else if (name === 'pan_no') {
    
    const alphanumericOnly = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setFormData(prev => ({
      ...prev,
      [name]: alphanumericOnly
    }));
  } else {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }
  
  if (errors[name]) {
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  }
};

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, GIF, WEBP)';
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return 'File size should be less than 5MB';
    }
    
    return null;
  };

  const validatePhoneNumber = (value) => {
  if (value.length === 0) {
    return null;
  }
  if (value.length !== 10 || !/^\d{10}$/.test(value)) {
    return 'Phone number must be exactly 10 digits';
  }
  return null;
};

 const validatePAN = (value) => {
    if (value === '') return '';
    if (value.length !== 10) {
      return 'PAN number must be exactly 10 characters';
    }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
      return 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    return '';
  };

  const processFile = (file) => {
    const error = validateFile(file);
    if (error) {
      setErrors(prev => ({
        ...prev,
        client_logo: error
      }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      
      setFormData(prev => ({
        ...prev,
        client_logo: base64String
      }));
      
      setLogoPreview(base64String);
    };
    reader.readAsDataURL(file);

    if (errors.client_logo) {
      setErrors(prev => ({
        ...prev,
        client_logo: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      client_logo: null
    }));
    setLogoPreview(null);
    
    const fileInput = document.getElementById('clientLogo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const createClient = async (clientData) => {
    try {
      const response = await fetch(`${API_URL}/clients/create_client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(clientData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || `HTTP error! status: ${response.status}` };
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
      
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to server. Please check if the backend is running on ${API_URL}`);
      }
      
      throw error;
    }
  };

  const updateClient = async (clientId, clientData) => {
    try {
      const response = await fetch(`${API_URL}/clients/update_client/${clientId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(clientData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || `HTTP error! status: ${response.status}` };
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
      
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to server. Please check if the backend is running on ${API_URL}`);
      }
      
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setErrors({});

    const phoneError = validatePhoneNumber(formData.contact_number);
    const panError = validatePAN(formData.pan_no);

    if (phoneError || panError) {
      setErrors({
        contact_number: phoneError,
        pan_no: panError
      });
      setIsLoading(false);
      return;
    }

    try {
      const clientData = {
        client_name: formData.client_name.trim(),
        gst_no: formData.gst_no.trim(),
        name: formData.name.trim(),
        contact_number: formData.contact_number.trim(),
        email: formData.email.trim(),
        pan_no: formData.pan_no.trim(),
        state: formData.state.trim(),
        address: formData.address.trim(),
        bank_name: formData.bank_name.trim(),
        bank_account_number: formData.bank_account_number.trim(),
        ifsc_code: formData.ifsc_code.trim(),
        client_logo: formData.client_logo
      };

      let result;
      let newClient;

      if (isEditMode && editClient) {
        result = await updateClient(editClient.client_id, clientData);
        
        newClient = {
          ...editClient,
          client_name: formData.client_name.trim(),
          gst_no: formData.gst_no.trim(),
          name: formData.name.trim(),
          contact_number: formData.contact_number.trim(),
          email: formData.email.trim(),
          pan_no: formData.pan_no.trim(),
          state: formData.state.trim(),
          address: formData.address.trim(),
          bank_name: formData.bank_name.trim(),
          bank_account_number: formData.bank_account_number.trim(),
          ifsc_code: formData.ifsc_code.trim(),
          client_logo: result.data?.client_logo || formData.client_logo,
          updated_on: new Date().toISOString(),
          originalData: result
        };

        alert('Client updated successfully!');
      } else {
        result = await createClient(clientData);
        
        newClient = {
          id: result.client_id || Date.now(),
          client_id: result.client_id || Date.now(),
          client_name: formData.client_name.trim(),
          gst_no: formData.gst_no.trim(),
          name: formData.name.trim(),
          contact_number: formData.contact_number.trim(),
          email: formData.email.trim(),
          pan_no: formData.pan_no.trim(),
          state: formData.state.trim(),
          address: formData.address.trim(),
          bank_name: formData.bank_name.trim(),
          bank_account_number: formData.bank_account_number.trim(),
          ifsc_code: formData.ifsc_code.trim(),
          client_logo: result.data?.client_logo || formData.client_logo,
          updated_on: new Date().toISOString(),
          originalData: result
        };

        alert('Client added successfully!');
      }

      if (onClientAdded) {
        onClientAdded(newClient);
      }
      
      if (!isEditMode) {
        setFormData({
          client_name: '',
          gst_no: '',
          name: '',
          contact_number: '',
          email: '',
          pan_no: '',
          state: '',
          address: '',
          bank_name: '',
          bank_account_number: '',
          ifsc_code: '',
          client_logo: null
        });
        setLogoPreview(null);
        
        const fileInput = document.getElementById('clientLogo');
        if (fileInput) {
          fileInput.value = '';
        }
      }

      if (onBack) {
        onBack();
      }

    } catch (error) {
      setErrors({
        submit: error.message || `Failed to ${isEditMode ? 'update' : 'create'} client. Please try again.`
      });
      
      alert('Error: ' + (error.message || `Failed to ${isEditMode ? 'update' : 'create'} client. Please try again.`));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <div className="CLI-00">
      <div className="CLI-01">
        <div className="CLI-02">
          <h1 className="CLI-03">
            {isEditMode ? 'Edit Client' : 'Add New Client'}
          </h1>
        </div>
        
        <div className="CLI-04">
          <div className="CLI-05">
            {errors.submit && (
              <div className="CLI-submit-error">
                {errors.submit}
              </div>
            )}

            <div className="CLI-68">
              <div className="CLI-12">
                <label className="CLI-13" htmlFor="client_name">
                  Client Name
                </label>
                <input
                  type="text"
                  id="client_name"
                  name="client_name"
                  className="CLI-15"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  placeholder="Enter Client Name"
                  disabled={isLoading}
                />
              </div>

              <div className="CLI-18">
                <label className="CLI-19" htmlFor="gst_no">
                  GSTIN
                </label>
                <input
                  type="text"
                  id="gst_no"
                  name="gst_no"
                  className="CLI-21"
                  value={formData.gst_no}
                  onChange={handleInputChange}
                  placeholder="Enter GSTIN"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="CLI-68">
              <div className="CLI-24">
                <label className="CLI-25" htmlFor="name">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="CLI-27"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter Contact Person Name"
                  disabled={isLoading}
                />
              </div>

              <div className="CLI-69">
                <label className="CLI-70" htmlFor="contact_number">
                  Contact Number
                </label>
                <input
                  type="text"
                  id="contact_number"
                  name="contact_number"
                  className="CLI-71"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  placeholder="Enter Contact Number"
                  disabled={isLoading}
                  maxLength="10"
                />
                {errors.contact_number && <span className="CLI-38">{errors.contact_number}</span>}
              </div>
            </div>

            <div className="CLI-68">
              <div className="CLI-72">
                <label className="CLI-73" htmlFor="email">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="CLI-74"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter Email Address"
                  disabled={isLoading}
                />
              </div>

              <div className="CLI-69">
                <label className="CLI-70" htmlFor="pan_no">
                  PAN No
                </label>
                <input
                  type="text"
                  id="pan_no"
                  name="pan_no"
                  className="CLI-71"
                  value={formData.pan_no}
                  onChange={handleInputChange}
                  placeholder="Enter PAN No"
                  disabled={isLoading}
                  maxLength="10"
                />
                {errors.pan_no && <span className="CLI-38">{errors.pan_no}</span>}
              </div>
            </div>

            <div className="CLI-68">
              <div className="CLI-72">
                <label className="CLI-73" htmlFor="state">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  className="CLI-74"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter State"
                  disabled={isLoading}
                />
              </div>

              <div className="CLI-59">
                <label className="CLI-60" htmlFor="bank_name">
                  Bank Name
                </label>
                <input
                  type="text"
                  id="bank_name"
                  name="bank_name"
                  className="CLI-61"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Name"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="CLI-68">
              <div className="CLI-62">
                <label className="CLI-63" htmlFor="bank_account_number">
                  Bank Account Number
                </label>
                <input
                  type="text"
                  id="bank_account_number"
                  name="bank_account_number"
                  className="CLI-64"
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Account Number"
                  disabled={isLoading}
                />
              </div>

              <div className="CLI-65">
                <label className="CLI-66" htmlFor="ifsc_code">
                  IFSC Code
                </label>
                <input
                  type="text"
                  id="ifsc_code"
                  name="ifsc_code"
                  className="CLI-67"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  placeholder="Enter IFSC Code"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="CLI-75">
              <label className="CLI-76" htmlFor="address">
                Address
              </label>
              <textarea
                id="address"
                name="address"
                className="CLI-77"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter Address"
                rows="3"
                disabled={isLoading}
              />
            </div>

            <div className="CLI-30">
              <label className="CLI-31" htmlFor="clientLogo">
                Client Logo
              </label>
              
              <div 
                className={`CLI-42 ${isDragOver ? 'CLI-43' : ''} ${errors.client_logo ? 'CLI-44' : ''} ${isLoading ? 'CLI-disabled' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => !isLoading && document.getElementById('clientLogo').click()}
              >
                <input
                  type="file"
                  id="clientLogo"
                  name="clientLogo"
                  className="CLI-58"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isLoading}
                />
                
                {!logoPreview ? (
                  <div className="CLI-45">
                    <div className="CLI-46">
                      <svg className="CLI-47" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                      </svg>
                    </div>
                    <div className="CLI-48">
                      <span className="CLI-49">Click to upload</span> or drag and drop
                    </div>
                    <div className="CLI-50">
                      PNG, JPG, GIF up to 5MB
                    </div>
                  </div>
                ) : (
                  <div className="CLI-51">
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="CLI-52"
                      onError={(e) => {
                        
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="CLI-53">
                      <button 
                        type="button" 
                        className="CLI-54" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage();
                        }} 
                        title="Remove image"
                        disabled={isLoading}
                      >
                        <svg className="CLI-55" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {errors.client_logo && <span className="CLI-38">{errors.client_logo}</span>}
            </div>
          </div>

          <div className="CLI-39">
            <button 
              type="button" 
              className="CLI-40" 
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </button>
            <button 
              type="button" 
              className="CLI-41" 
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading 
                ? (isEditMode ? 'Updating...' : 'Creating...') 
                : (isEditMode ? 'Update' : 'Submit')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Client;